import { useCallback, useEffect, useRef, useState } from 'react'

import { isApiError } from '../../api/errors'
import { deleteReferenceImage, type ReferenceImageItem } from '../../api/referenceImages'
import { resolveAbsoluteHttpImageUrl } from '../../api/referenceUpload'
import { useToast } from '../../context/ToastContext'
import { usePreferHoverChromeTooltips } from '../../hooks/usePreferHoverChromeTooltips'
import { referenceImageThumbnailUrl, type ReferenceImageUrlHints } from '../../lib/referenceImageUrl'
import { type RefreshUrlExecutor } from '../../lib/referenceUrlRefresh'
import {
  referenceImageHintsFromItem,
  resolveMediaImage,
  resolveReferenceImageFromItem,
} from '../../lib/resolvedMediaImage'
import { ChromeToolbarButton, ChromeToolbarDangerButton } from '../ChromeActions'
import {
  chromeIconButtonClass,
  chromePrimaryCompactClass,
  chromeSelectableMediaCardClass,
  chromeSelectableMediaCornerBadgeClass,
  chromeToggleChipClass,
  chromeToolbarButtonClass,
} from '../ChromeSurface'
import { FloatingRemoveBubble } from '../FloatingRemoveBubble'
import { ThemeModal } from '../modal'
import { ThemedImage } from '../ThemedImage'

/** 与创作页、漫画参考图一致：用于比对「已添加」与生图提交 */
export function referencePickerSubmitUrl(
  raw: string,
  hints?: { bucket?: string; objectKey?: string },
): string {
  return resolveAbsoluteHttpImageUrl(raw, hints)
}

function LibraryPickerReferenceTile({
  item,
  angular,
  submitUrl,
  imgUrl,
  selected,
  parentInactive,
  alreadyInComposer,
  onToggle,
  onThumbBroken,
  onRequestDelete,
  hints,
  transformUrl,
  refreshExecutor,
  onUrlRefreshed,
}: {
  item: ReferenceImageItem
  angular: boolean
  submitUrl: string
  imgUrl: string
  selected: boolean
  parentInactive: boolean
  alreadyInComposer: boolean
  onToggle: (item: ReferenceImageItem) => void
  onThumbBroken: (publicId: string) => void
  /** 加载失败时点击删除气泡：仅上报意图，确认弹窗由父级渲染 */
  onRequestDelete: () => void
  hints: ReferenceImageUrlHints
  transformUrl: (rawUrl: string, hints?: ReferenceImageUrlHints) => string
  /** 自定义补签执行器；未传则默认调后端 refresh-url */
  refreshExecutor?: RefreshUrlExecutor
  onUrlRefreshed?: (item: ReferenceImageItem) => void
}) {
  const [mediaBroken, setMediaBroken] = useState(false)
  /** 重挂载 ThemedImage 以重试加载（key 变化即重新发起请求） */
  const [reloadNonce, setReloadNonce] = useState(0)
  const preferHoverTitles = usePreferHoverChromeTooltips()

  useEffect(() => {
    setMediaBroken(false)
  }, [submitUrl])

  const tileDisabled = parentInactive || mediaBroken

  const reloadTile = () => {
    setMediaBroken(false)
    setReloadNonce((n) => n + 1)
  }

  return (
    <div className={['relative min-h-[8rem]', angular ? 'rounded-none' : 'rounded-xl'].join(' ')}>
      <div
        role="button"
        tabIndex={tileDisabled ? -1 : 0}
        aria-disabled={tileDisabled}
        onClick={() => {
          if (!tileDisabled) onToggle(item)
        }}
        onKeyDown={(e) => {
          if (tileDisabled) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle(item)
          }
        }}
        className={[
          chromeSelectableMediaCardClass(angular, selected && !tileDisabled),
          'group relative min-h-[8rem] w-full cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50',
          tileDisabled ? 'cursor-not-allowed opacity-55' : '',
        ].join(' ')}
        aria-pressed={selected}
        aria-label={alreadyInComposer ? `${item.name}，已使用` : mediaBroken ? '加载失败' : item.name}
        title={preferHoverTitles && !mediaBroken ? item.name : undefined}
      >
        {/* 失败态走极简占位：底部标签与左上重载按钮由本 tile 自己渲染，避免两套失败 UI 叠加 */}
        <ThemedImage
          key={reloadNonce}
          src={imgUrl}
          alt=""
          angular={angular}
          compact
          loading="lazy"
          decoding="async"
          objectKey={item.objectKey}
          urlHints={hints}
          transformUrl={transformUrl}
          refreshExecutor={refreshExecutor}
          onUrlRefreshed={onUrlRefreshed}
          minimalFallback
          className="pointer-events-none absolute inset-0 z-0 min-h-[7.5rem] h-full w-full object-cover transition group-hover:brightness-[1.04]"
          fallbackClassName="pointer-events-none absolute inset-0 z-0 flex h-full min-h-[7.5rem] w-full flex-col justify-center rounded-none bg-surface-muted"
          onFailedChange={(failed) => {
            setMediaBroken(failed)
            if (failed) onThumbBroken(item.publicId)
          }}
        />
        {/* 选中描边必须用覆盖层：卡片的 inset box-shadow 会被全幅铺底的绝对定位图片盖住，用户看不出选中态 */}
        {selected && !tileDisabled ? (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 z-[2] rounded-[inherit] ring-2 ring-inset ring-accent"
          />
        ) : null}
        {!mediaBroken ? (
          <span
            className={chromeSelectableMediaCornerBadgeClass(
              angular,
              selected && !tileDisabled ? 'selected' : alreadyInComposer ? 'saved' : 'idle',
            )}
          >
            {selected && !tileDisabled ? '已选' : alreadyInComposer ? '已添加' : '选择'}
          </span>
        ) : null}
        <span className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] bg-[color-mix(in_srgb,var(--panel)_82%,transparent)] px-2 py-1 text-[11px] font-medium text-ink">
          <span className="block truncate">
            {alreadyInComposer ? '已添加' : mediaBroken ? '加载失败' : item.name}
          </span>
        </span>
      </div>
      {mediaBroken ? (
        <>
          <button
            type="button"
            className={[
              chromeIconButtonClass(angular),
              'pointer-events-auto absolute left-1 top-1 z-10',
            ].join(' ')}
            aria-label="重新加载该图片"
            title="重新加载"
            onClick={(e) => {
              e.stopPropagation()
              reloadTile()
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="size-4"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
          </button>
          <FloatingRemoveBubble
            angular={angular}
            tooltipContent="从图库删除该文件"
            className="pointer-events-auto absolute right-1 top-1 z-10"
            aria-label="从图库删除加载失败的图片"
            onClick={(e) => {
              e.stopPropagation()
              onRequestDelete()
            }}
          />
        </>
      ) : null}
    </div>
  )
}

/** 选择器顶部分区：上传记录（本地磁盘子树，默认选中）与图库（云） */
export type LibraryReferencePickerTab = 'gallery' | 'recent'

const PICKER_TABS: ReadonlyArray<{ key: LibraryReferencePickerTab; label: string }> = [
  { key: 'recent', label: '上传记录' },
  { key: 'gallery', label: '图库' },
]

export type LibraryReferencePickerModalProps = {
  angular: boolean
  open: boolean
  loading: boolean
  items: ReferenceImageItem[]
  /** 当前分区；`recent` 时渲染 {@link recentItems} */
  activeTab?: LibraryReferencePickerTab
  /** 「最近上传」分区条目；未加载时为 undefined */
  recentItems?: ReferenceImageItem[]
  /** 「最近上传」分区首屏加载中 */
  recentLoading?: boolean
  /** 当前分区是否还有下一页（决定是否挂载滚动哨兵） */
  hasMore?: boolean
  /** 切换到指定分区（由父级按需拉数据） */
  onTabChange?: (tab: LibraryReferencePickerTab) => void
  /** 列表触底：追加下一页 */
  onLoadMore?: () => void
  selectedIds: string[]
  /** 还可选几张（multi）或 1（single） */
  remaining: number
  /** 当前已使用的 URL，用于标记「已添加」 */
  referenceUrls: string[]
  selectionMode?: 'single' | 'multi'
  title?: string
  confirmLabel?: string
  /** 清空当前已选；未传则不渲染「清空」按钮 */
  onClearSelection?: () => void
  onClose: () => void
  onToggle: (item: ReferenceImageItem) => void
  onUseLocalUpload: () => void
  onConfirm: () => void
  onThumbBroken: (publicId: string) => void
  onDeletedFromLibrary: (publicId: string) => void
  runBlockingDelete: (action: () => Promise<void>) => Promise<unknown>
  /**
   * 缩略图裂了时的补签执行器；未传则默认直接调后端 refresh-url。
   * 传值可用于把新 url 一并回写到父级列表，避免下次打开仍拿旧签名。
   */
  refreshExecutor?: RefreshUrlExecutor
  onUrlRefreshed?: (item: ReferenceImageItem) => void
}

/** 创作页 / 漫画 / 分镜共用的图库参考图选择器 */
export function LibraryReferencePickerModal({
  angular,
  open,
  loading,
  items,
  activeTab = 'recent',
  recentItems,
  recentLoading = false,
  hasMore = false,
  selectedIds,
  remaining,
  referenceUrls,
  selectionMode = 'multi',
  title = '选择参考图',
  confirmLabel,
  onClearSelection,
  onClose,
  onToggle,
  onUseLocalUpload,
  onConfirm,
  onTabChange,
  onLoadMore,
  onThumbBroken,
  onDeletedFromLibrary,
  runBlockingDelete,
  refreshExecutor,
  onUrlRefreshed,
}: LibraryReferencePickerModalProps) {
  const selected = new Set(selectedIds)

  /** 稳定引用：缩略图 URL 的构造方式，补签后的新 URL 走同一套处理 */
  const pickerTransformUrl = useCallback(
    (rawUrl: string, hints?: ReferenceImageUrlHints) =>
      referenceImageThumbnailUrl(resolveMediaImage(rawUrl, hints).displayUrl, 360, hints),
    [],
  )
  const existing = new Set(
    referenceUrls.map((url) => referencePickerSubmitUrl(url)),
  )
  const confirmText =
    confirmLabel ?? (selectionMode === 'single' ? '使用此图' : '添加选中')

  /** 待确认删除的坏图；确认弹窗沿用 CreatePage 的 ThemeModal 二次确认范式 */
  const [pendingDelete, setPendingDelete] = useState<ReferenceImageItem | null>(null)
  const { showToast } = useToast()

  const executePendingDelete = () => {
    const target = pendingDelete
    if (!target) return
    setPendingDelete(null)
    void runBlockingDelete(async () => {
      try {
        await deleteReferenceImage(target.publicId)
        showToast({ message: '已从图库删除该文件', variant: 'success', autoCloseMs: 4000 })
        onDeletedFromLibrary(target.publicId)
      } catch (e) {
        showToast({
          message: isApiError(e) ? e.message : e instanceof Error ? e.message : '删除失败，请稍后重试',
          variant: 'error',
        })
      }
    })
  }

  const galleryItems = items
  const visibleItems = activeTab === 'recent' ? recentItems ?? [] : galleryItems
  const listLoading = activeTab === 'recent' ? recentLoading : loading

  const loadMoreRef = useRef(onLoadMore)
  useEffect(() => {
    loadMoreRef.current = onLoadMore
  }, [onLoadMore])

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const sentinelActive = open && hasMore && visibleItems.length > 0
  useEffect(() => {
    const node = sentinelRef.current
    if (!sentinelActive || !node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current?.()
      },
      { rootMargin: '320px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [sentinelActive, visibleItems.length])

  return (
    <>
      <ThemeModal
      open={open}
      onClose={onClose}
      title={title}
      angular={angular}
      size="lg"
      bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <button type="button" className={chromeToolbarButtonClass(angular)} onClick={onUseLocalUpload}>
            本地上传
          </button>
          <div className="flex items-center gap-2">
            {selectionMode === 'multi' ? (
              <span className="text-xs text-ink-soft tabular-nums">
                已选 {selectedIds.length} 张，可再选 {Math.max(0, remaining - selectedIds.length)} 张
              </span>
            ) : (
              <span className="text-xs text-ink-soft tabular-nums">
                {selectedIds.length > 0 ? '已选 1 张' : '请选择一张'}
              </span>
            )}
            <button
              type="button"
              disabled={selectedIds.length === 0}
              className={chromePrimaryCompactClass(angular)}
              onClick={onConfirm}
            >
              {confirmText}
            </button>
          </div>
        </div>
      }
    >
      <div className="mb-3 flex items-center gap-1.5" role="tablist" aria-label="参考图来源">
        {PICKER_TABS.map((entry) => {
          const active = entry.key === activeTab
          return (
            <button
              key={entry.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onTabChange?.(entry.key)}
              className={chromeToggleChipClass(angular, active)}
            >
              {entry.label}
            </button>
          )
        })}
        {onClearSelection ? (
          <button
            type="button"
            className={[chromeToolbarButtonClass(angular), 'ml-auto'].join(' ')}
            disabled={selectedIds.length === 0}
            onClick={onClearSelection}
            aria-label="清空已选参考图"
          >
            清空
          </button>
        ) : null}
      </div>
      {listLoading && visibleItems.length === 0 ? (
        <div className="rounded-[var(--radius-sm)] border border-line bg-surface-muted px-3 py-4 text-sm text-ink-soft">
          正在加载…
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="rounded-[var(--radius-sm)] border border-dashed border-line bg-surface-muted px-3 py-6 text-center text-sm text-ink-soft">
          {activeTab === 'recent' ? '暂无上传记录' : '图库暂无图片'}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {visibleItems.map((item) => {
            const submitUrl = referencePickerSubmitUrl(item.url, {
              bucket: item.bucket,
              objectKey: item.objectKey,
            })
            const alreadyInComposer = existing.has(submitUrl)
            const parentInactive =
              alreadyInComposer ||
              (!selected.has(item.publicId) && selectedIds.length >= remaining)
            const hints = referenceImageHintsFromItem(item)
            const imgUrl = referenceImageThumbnailUrl(
              resolveReferenceImageFromItem(item).displayUrl,
              360,
              hints,
            )
            return (
              <LibraryPickerReferenceTile
                key={item.publicId}
                item={item}
                angular={angular}
                submitUrl={submitUrl}
                imgUrl={imgUrl}
                selected={selected.has(item.publicId)}
                parentInactive={parentInactive}
                alreadyInComposer={alreadyInComposer}
                onToggle={onToggle}
                onThumbBroken={onThumbBroken}
                onRequestDelete={() => setPendingDelete(item)}
                hints={hints}
                transformUrl={pickerTransformUrl}
                refreshExecutor={refreshExecutor}
                onUrlRefreshed={onUrlRefreshed}
              />
            )
          })}
        </div>
      )}
      {sentinelActive ? (
        <div ref={sentinelRef} className="h-px w-full" aria-hidden="true" />
      ) : null}
      </ThemeModal>
      <ThemeModal
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        title="确认删除？"
        angular={angular}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setPendingDelete(null)}>
              取消
            </ChromeToolbarButton>
            <ChromeToolbarDangerButton type="button" onClick={executePendingDelete}>
              确认删除
            </ChromeToolbarDangerButton>
          </div>
        }
      >
        <p className="text-sm leading-relaxed text-ink">
          将从图库删除「{pendingDelete?.name ?? ''}」，删除后不可恢复。
        </p>
      </ThemeModal>
    </>
  )
}
